/**
 * CompetitionLanding.jsx (iter226) — Public entry to the Stockaris
 * Competition Demo. Presents the founder story, the challenge intro, and
 * two CTAs: START THE WAREHOUSE CHALLENGE / EXPLORE STOCKARIS.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Warehouse, PlayCircle, Compass, Trophy, Lightning, ShieldCheck,
} from '@phosphor-icons/react';
import { bootstrapCompetitionDemo, trackCompetitionEvent } from '../lib/api';

export default function CompetitionLanding() {
  const navigate = useNavigate();

  useEffect(() => {
    // Fire and forget — bootstrap the demo tenant on first visit so the
    // login flow is instant.
    bootstrapCompetitionDemo().catch(() => {});
    trackCompetitionEvent('demo.landing_viewed').catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 selection:bg-emerald-400/30" data-testid="competition-landing">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,#065f4640,transparent_50%),radial-gradient(circle_at_90%_20%,#0284c740,transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-24">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.4em] text-emerald-400 mb-6">
            <Warehouse size={16} weight="duotone" />
            Stockaris · Competition Demo
          </div>
          <h1 className="text-5xl md:text-7xl font-black leading-[1.02] tracking-tight max-w-4xl">
            STOCKARIS<br />
            <span className="text-emerald-400">COMPETITION DEMO</span>
          </h1>
          <p className="mt-6 text-xl text-neutral-300 max-w-2xl">
            Experience warehouse management from the warehouse floor.
          </p>

          <div className="mt-10 max-w-3xl border-l-4 border-emerald-500 pl-6">
            <p className="text-lg text-neutral-200 leading-relaxed italic">
              «Stockaris was built from more than 20 years of real-world warehouse
              experience to close the gap between what a WMS says should happen
              and what actually happens on the warehouse floor.»
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={() => {
                trackCompetitionEvent('demo.cta_start_challenge_clicked').catch(() => {});
                navigate('/competition-demo/login');
              }}
              className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-black text-sm tracking-widest uppercase px-8 py-6"
              data-testid="cd-start-challenge-btn"
            >
              <PlayCircle size={20} weight="fill" className="mr-2" />
              Start the Warehouse Challenge
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                trackCompetitionEvent('demo.cta_explore_clicked').catch(() => {});
                navigate('/competition-demo/login');
              }}
              className="rounded-none border-neutral-700 hover:border-emerald-500 hover:text-emerald-400 bg-transparent text-neutral-100 font-black text-sm tracking-widest uppercase px-8 py-6"
              data-testid="cd-explore-btn"
            >
              <Compass size={20} weight="duotone" className="mr-2" />
              Explore Stockaris
            </Button>
          </div>
        </div>
      </div>

      {/* Three pillars */}
      <div className="max-w-6xl mx-auto px-6 py-16 border-t border-neutral-900">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500 mb-8">
          Why Stockaris is different
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
          <div className="p-8 bg-neutral-900/60 border-l-4 border-emerald-500">
            <Trophy size={28} weight="duotone" className="text-emerald-400 mb-4" />
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">Inventory Intelligence</div>
            <p className="text-neutral-200">Don't just find the discrepancy. Find where to look.</p>
          </div>
          <div className="p-8 bg-neutral-900/60 border-l-4 border-sky-500">
            <Lightning size={28} weight="duotone" className="text-sky-400 mb-4" />
            <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-2">Warehouse-Floor Control</div>
            <p className="text-neutral-200">Put the right tools in the hands of the people doing the work.</p>
          </div>
          <div className="p-8 bg-neutral-900/60 border-l-4 border-amber-500">
            <ShieldCheck size={28} weight="duotone" className="text-amber-400 mb-4" />
            <div className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-2">Proactive Planning</div>
            <p className="text-neutral-200">Identify capacity, inventory and operational problems before they become expensive problems.</p>
          </div>
        </div>
      </div>

      {/* Founder story */}
      <div className="max-w-6xl mx-auto px-6 py-16 border-t border-neutral-900">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500 mb-4">
          Why Stockaris exists
        </div>
        <div className="max-w-3xl space-y-4 text-neutral-300 leading-relaxed">
          <p>«Stockaris wasn't created because I wanted to build another WMS.</p>
          <p>It was created because I've spent more than 20 years working in warehouses and seeing the gaps between what a WMS says should happen and what actually happens on the warehouse floor.</p>
          <p>I've seen the problems that appear after implementation, after the support period ends, and when warehouse teams have to find workarounds just to get the job done.</p>
          <p>Stockaris was built to close that gap.»</p>
        </div>
      </div>

      <div className="border-t border-neutral-900 py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between text-xs text-neutral-500 font-mono uppercase tracking-widest">
          <span>Isolated demo · No production data</span>
          <span>Emergent Build Competition · 2026</span>
        </div>
      </div>
    </div>
  );
}
